import React, { useState, useEffect } from 'react';
import {
  Card,
  Typography,
  Space,
  Button,
  Row,
  Col,
  Statistic,
  Tag,
  Select,
  message,
  Descriptions,
  Badge,
} from 'antd';
import {
  RocketOutlined,
  SafetyOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  FileTextOutlined,
  HistoryOutlined,
} from '@ant-design/icons';
import { useTranslation } from "react-i18next";
// import { Typography } from 'antd/lib';
import WorkflowTimelineAntd from '@/components/WorkflowTimeline/WorkflowTimelineAntd';
import ESignatureModalAntd from '@/components/ESignatureModal/ESignatureModalAntd';
import {
  RECIPE_VERSIONING_WORKFLOW,
  RECIPE_WORKFLOW_SUMMARY,
  APPROVAL_MATRIX,
} from '@/constants/workflowConstants';
import MainCard from '@/components/MainCard';
import { defaultColors } from '@/themes/antd-theme';
import { useTheme } from '@/App';
import './RecipeVersioning.css';

const { Title, Text } = Typography;
const { Option } = Select;

/**
 * RecipeVersioning Page
 * Manages Recipe Change Control and Versioning Workflow
 * 21 CFR Part 11 Compliant
 */
const RecipeVersioning = () => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const [currentStage, setCurrentStage] = useState(1);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [workflowData, setWorkflowData] = useState(null);
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [selectedStageForApproval, setSelectedStageForApproval] = useState(null);

  // Dark mode colors
  const cardBgColor = isDark ? defaultColors.palette.gray[800] : '#ffffff';
  const textColor = isDark ? '#ffffff' : '#1f2937';
  const secondaryTextColor = isDark ? defaultColors.palette.gray[400] : '#6b7280';
  const borderColor = isDark ? defaultColors.palette.gray[600] : '#e5e7eb';

  // Mock recipe data - Replace with actual API call
  const mockRecipes = [
    {
      id: 1,
      name: 'ASPIRIN-100MG-TAB',
      version: '2.1',
      status: 'Under Change Control',
      changeId: 'CR-2024-001',
    },
    {
      id: 2,
      name: 'PARACETAMOL-500MG-CAP',
      version: '1.5',
      status: 'Draft',
      changeId: 'CR-2024-002',
    },
    {
      id: 3,
      name: 'IBUPROFEN-200MG-TAB',
      version: '3.0',
      status: 'QA Review',
      changeId: 'CR-2024-003',
    },
  ];

  useEffect(() => {
    // Load selected recipe workflow data
    if (selectedRecipe) {
      loadWorkflowData(selectedRecipe);
    }
  }, [selectedRecipe]);

  const loadWorkflowData = (recipeId) => {
    // Mock API call - Replace with actual API
    const mockData = {
      recipeId: recipeId,
      currentStage: 2,
      stage1_data: {
        approver: 'John Smith (R&D Manager)',
        date: '2024-01-15',
        status: 'Approved',
      },
    };
    setWorkflowData(mockData);
    setCurrentStage(mockData.currentStage);
  };

  const handleRecipeSelect = (value) => {
    setSelectedRecipe(value);
    message.info(`Loading workflow for Recipe ID: ${value}`);
  };

  const handleApprovalClick = (stage) => {
    setSelectedStageForApproval(stage);
    setSignatureModalOpen(true);
  };

  const handleSignatureSubmit = async (signatureData) => {
    try {
      // Mock API call to save signature and progress workflow
      console.log('Signature Data:', signatureData);

      message.success({
        content: `Stage ${selectedStageForApproval.stage} approved successfully!`,
        duration: 3,
      });

      // Progress to next stage
      setCurrentStage((prev) => Math.min(prev + 1, RECIPE_VERSIONING_WORKFLOW.length));

      setSignatureModalOpen(false);
      setSelectedStageForApproval(null);
    } catch (error) {
      message.error('Failed to submit signature. Please try again.');
      throw error;
    }
  };

  const handleStageAction = (action, stage) => {
    message.info(`Action: ${action} for stage ${stage.stage}`);
    // Implement additional actions as needed
  };

  return (
    <MainCard
      className="m-2.5 border border-gray-300 shadow-md"
      title={
        <Space align="center">
          <div className='flex items-center gap-3'>
            <RocketOutlined className="ml-4 text-blue-600" style={{ fontSize: 24 }} />
            <Title level={3} style={{ margin: 0 }}>
              {t("Recipe Versioning & Change Control")}
            </Title>
          </div>
        </Space>
      }
      content={false}
    >
      <div style={{ padding: 24 }}>
        {/* Header Stats */}
        <Card
          style={{ marginBottom: 24, backgroundColor: isDark ? '#0F172A' : defaultColors.primary }}
        >
          <Row gutter={[24, 24]}>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title={<Text style={{ color: 'rgba(255,255,255,0.85)' }}>Total Stages</Text>}
                value={RECIPE_WORKFLOW_SUMMARY.totalStages}
                prefix={<FileTextOutlined style={{ color: '#fff' }} />}
                valueStyle={{ color: '#fff' }}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title={
                  <Text style={{ color: 'rgba(255,255,255,0.85)' }}>E-Signatures Required</Text>
                }
                value={RECIPE_WORKFLOW_SUMMARY.mandatoryElectronicSignatures}
                prefix={<SafetyOutlined style={{ color: '#fff' }} />}
                valueStyle={{ color: '#fff' }}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title={<Text style={{ color: 'rgba(255,255,255,0.85)' }}>Audit Trail Points</Text>}
                value={RECIPE_WORKFLOW_SUMMARY.auditTrailPoints}
                prefix={<HistoryOutlined style={{ color: '#fff' }} />}
                valueStyle={{ color: '#fff' }}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title={<Text style={{ color: 'rgba(255,255,255,0.85)' }}>Avg Duration</Text>}
                value={RECIPE_WORKFLOW_SUMMARY.averageDuration}
                prefix={<ClockCircleOutlined style={{ color: '#fff' }} />}
                valueStyle={{ color: '#fff', fontSize: 16 }}
              />
            </Col>
          </Row>
        </Card>

        {/* Recipe Selection */}
        <Card
          title={<span style={{ color: textColor }}>Select Recipe for Change Control</span>}
          style={{ marginBottom: 24, backgroundColor: cardBgColor, borderColor }}
        >
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <Select
              style={{ width: '100%' }}
              placeholder="Select a recipe to manage"
              onChange={handleRecipeSelect}
              size="large"
              showSearch
              optionFilterProp="children"
            >
              {mockRecipes.map((recipe) => (
                <Option key={recipe.id} value={recipe.id}>
                  <Space>
                    <Text strong>{recipe.name}</Text>
                    <Tag color="blue">v{recipe.version}</Tag>
                    <Tag color={recipe.status === 'Draft' ? 'default' : 'processing'}>
                      {recipe.status}
                    </Tag>
                  </Space>
                </Option>
              ))}
            </Select>

            {selectedRecipe && (
              <Descriptions
                bordered
                size="small"
                column={{ xs: 1, sm: 2, md: 3 }}
                labelStyle={{
                  color: secondaryTextColor,
                  backgroundColor: isDark ? defaultColors.palette.gray[700] : undefined,
                }}
                contentStyle={{
                  color: textColor,
                  backgroundColor: isDark ? defaultColors.palette.gray[800] : undefined,
                }}
              >
                <Descriptions.Item label="Recipe ID">
                  {mockRecipes.find((r) => r.id === selectedRecipe)?.id}
                </Descriptions.Item>
                <Descriptions.Item label="Current Version">
                  <Tag color="blue">
                    v{mockRecipes.find((r) => r.id === selectedRecipe)?.version}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Change Control ID">
                  {mockRecipes.find((r) => r.id === selectedRecipe)?.changeId}
                </Descriptions.Item>
                <Descriptions.Item label="Status">
                  <Badge
                    status="processing"
                    text={
                      <span style={{ color: textColor }}>
                        {mockRecipes.find((r) => r.id === selectedRecipe)?.status}
                      </span>
                    }
                  />
                </Descriptions.Item>
                <Descriptions.Item label="Current Stage">
                  <Tag color="processing">
                    Stage {currentStage} - {RECIPE_VERSIONING_WORKFLOW[currentStage - 1]?.stageName}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Compliance">
                  <Tag color="success" icon={<CheckCircleOutlined />}>
                    21 CFR Part 11
                  </Tag>
                </Descriptions.Item>
              </Descriptions>
            )}
          </Space>
        </Card>

        {/* Workflow Timeline */}
        {selectedRecipe ? (
          <Card
            title={<span style={{ color: textColor }}>Change Control Workflow</span>}
            style={{ marginBottom: 24, backgroundColor: cardBgColor, borderColor }}
          >
            <WorkflowTimelineAntd
              workflow={RECIPE_VERSIONING_WORKFLOW}
              currentStage={currentStage}
              workflowData={workflowData}
              onStageAction={handleStageAction}
              onApprovalClick={handleApprovalClick}
              isDark={isDark}
            />
          </Card>
        ) : (
          <Card style={{ backgroundColor: cardBgColor, borderColor }}>
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              <FileTextOutlined
                style={{ fontSize: 64, color: secondaryTextColor, marginBottom: 16 }}
              />
              <Title level={4} style={{ color: secondaryTextColor }}>
                No Recipe Selected
              </Title>
              <Text style={{ color: secondaryTextColor }}>
                Please select a recipe from the dropdown above to view and manage its change control
                workflow.
              </Text>
            </div>
          </Card>
        )}

        {/* E-Signature Modal */}
        <ESignatureModalAntd
          open={signatureModalOpen}
          onClose={() => {
            setSignatureModalOpen(false);
            setSelectedStageForApproval(null);
          }}
          onSubmit={handleSignatureSubmit}
          stage={selectedStageForApproval}
          documentInfo={{
            recipeId: selectedRecipe,
            recipeName: mockRecipes.find((r) => r.id === selectedRecipe)?.name,
            version: mockRecipes.find((r) => r.id === selectedRecipe)?.version,
          }}
          requireMultipleSignatures={true}
          requiredSigners={
            selectedStageForApproval
              ? APPROVAL_MATRIX.recipe_versioning[
                  `stage${
                    selectedStageForApproval.stage
                  }_${selectedStageForApproval.stageCode.toLowerCase()}`
                ] || []
              : []
          }
        />
      </div>
    </MainCard>
  );
};

export default RecipeVersioning;
